import React, { useState, useMemo, useEffect } from 'react';
import ReportLayout, { DateRangePicker } from './components/ReportLayout';
import { BarChart3, Building2, ChevronDown } from 'lucide-react';
import useRevenueSummary from '@/hooks/useRevenueSummary';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

const PERIOD_MAP = {
  today: 'today',
  yesterday: 'yesterday',
  thisWeek: 'this_week',
  thisMonth: 'this_month',
  lastMonth: 'last_month',
};

export default function RevenueSummaryReport() {

 const { hasPermission } = useAuth();

if (!hasPermission(P.REPORTS.FINANCIAL.SCREEN)) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Access Denied
        </h3>
        <p className="text-sm text-slate-500">
          You don't have permission to view this page.
        </p>
      </div>
    </div>
  );
}

  const today = new Date().toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedPreset, setSelectedPreset] = useState('today');

  const [isCustomDate, setIsCustomDate] = useState(false);


  const [sortColumn, setSortColumn] = useState('period');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');

  const exportToCsv = () => {
    if (!data?.length) return;

    const headers = columns.map(c => `"${c.label}"`).join(',');
    const rows = data.map(r =>
      columns.map(c => `"${r[c.key] ?? ''}"`).join(',')
    );

    const blob = new Blob([headers, '\n', rows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'revenue-summary-report.csv';
    link.click();
  };

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const {
    data,
    summary,
    branchSummary,
    total,
    loading,
    filters,
    setFilters,
    fetchRevenueSummary,
  } = useRevenueSummary({
    offset: 0,
    limit: 25
  });


  useEffect(() => {
    const initFilters = { period: PERIOD_MAP['today'], offset: 0, limit: pageSize };
    setFilters(initFilters);
    fetchRevenueSummary(initFilters);
  }, []);


  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

  const summaryCards = useMemo(() => [
    { title: 'Total Orders', value: (summary?.totalOrders ?? 0).toLocaleString(), icon: BarChart3 },
    { title: 'Gross Sales', value: `₹${(summary?.grossSales ?? 0).toLocaleString()}`, icon: BarChart3 },
    { title: 'Net Sales', value: `₹${(summary?.netSales ?? 0).toLocaleString()}`, icon: BarChart3 },
    { title: 'Avg Order', value: `₹${(summary?.avgOrder ?? 0).toLocaleString()}`, icon: BarChart3 },
  ], [summary]);


  const columns = [
    { key: 'period', label: 'Period', sortable: true },
    { key: 'orders', label: 'Orders', sortable: true, align: 'right' },
    { key: 'grossSales', label: 'Gross Sales', sortable: true, align: 'right', render: v => `₹${v?.toLocaleString?.()}` },
    { key: 'discounts', label: 'Discounts', sortable: true, align: 'right', render: v => `₹${v?.toLocaleString?.()}` },
    { key: 'netSales', label: 'Net Sales', sortable: true, align: 'right', render: v => `₹${v?.toLocaleString?.()}` },
    { key: 'tax', label: 'Tax', sortable: true, align: 'right', render: v => `₹${v?.toLocaleString?.()}` },
    { key: 'avgOrder', label: 'Avg Order', sortable: true, align: 'right', render: v => `₹${v?.toLocaleString?.()}` },
  ];


  const handleRefresh = () => {
    const newFilters = {
      startDate,
      endDate,
      branchId: selectedBranchId || undefined,
      offset: 0,
      limit: pageSize
    };
    setFilters(newFilters);
    fetchRevenueSummary(newFilters);
  };


  const filtersUI = (
    <div className="flex items-center gap-3 flex-wrap">

      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        selectedPreset={selectedPreset}


        onPresetSelect={(preset) => {
          setSelectedPreset(preset);
          setIsCustomDate(false);

          const newFilters = {
            period: PERIOD_MAP[preset],
            branchId: selectedBranchId || undefined,
            offset: 0,
            limit: pageSize,
          };
          setFilters(newFilters);
          fetchRevenueSummary(newFilters);
        }}


        onStartDateChange={(d) => {
          setStartDate(d);
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}

        onEndDateChange={(d) => {
          setEndDate(d);
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}
      />

      <button
        onClick={handleRefresh}
        disabled={!isCustomDate}
        className={`
          h-9 px-4 rounded-lg text-sm font-medium whitespace-nowrap
          ${!isCustomDate
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-[#0FA29A] hover:bg-[#0D928A] text-white'
          }
        `}
      >
        Apply Filters
      </button>

      {branchOptions.length > 0 && (
        <div className="relative">
          <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <select
            value={selectedBranchId ?? ''}
            onChange={(e) => {
              const val = e.target.value || null;
              setSelectedBranchId(val);
              setCurrentPage(1);
              const newFilters = { ...filters, branchId: val || undefined, offset: 0 };
              setFilters(newFilters);
              fetchRevenueSummary(newFilters);
            }}
            className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
          >
            <option value="">All Branches</option>
            {branchOptions.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      )}

    </div>
  );


  const pagination = {
    currentPage,
    totalPages: Math.ceil(total / pageSize),
    totalItems: total,
    pageSize,
    onPageChange: (page) => {
      setCurrentPage(page);

      fetchRevenueSummary({
        ...filters,
        offset: (page - 1) * pageSize,
      });
    },
    onPageSizeChange: (size) => {
      setPageSize(size);
      setCurrentPage(1);

      fetchRevenueSummary({
        ...filters,
        offset: 0,
        limit: size,
      });
    }
  };

  //SORTING
const sortedData = useMemo(() => {
  if (!data?.length || !sortColumn) return data;

  const sorted = [...data];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === 'asc'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
  });

  return sorted;
}, [data, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;
  const query = searchQuery.toLowerCase().trim();
  return sortedData.filter(row => {
    // Search in period
    if (String(row.period || '').toLowerCase().includes(query)) return true;
    // Search in numeric fields
    const numericFields = ['orders', 'grossSales', 'discounts', 'netSales', 'tax', 'avgOrder'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
      }
    }
    return false;
  });
}, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Revenue Summary"
      description="Weekly/monthly revenue trends and comparisons"
      icon={BarChart3}
      exportPermission={P.REPORTS.FINANCIAL.REVENUE_EXPORT}
      color="emerald"
      category="Financial"
      categoryId="financial"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filtersUI}
      loading={loading}
      pagination={pagination}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={(column) => {
      let nextDirection;

      if (sortColumn === column) {
        nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        nextDirection = 'desc';
     }
      setSortColumn(column);
      setSortDirection(nextDirection);
    }}

      onRefresh={handleRefresh}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by period, sales..."
      onExport={(format) => {
          if (format === 'csv') exportToCsv();
      }}
    />
  );
}
